// insurance-information.component.ts
import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormConfigService } from '../forms-config.service';
import { AppStateService } from '../../app-state.service';
import { MaterialModule } from '../../material.module';

// Import the PDF generator service
import { InsurancePdfGenerator } from './insurance-pdf-generator.service';

interface GeneralInfo {
  carrier: string;
  employer: string;
  feeSchedule: string;
  groupNumber: string;
  groupName: string;
  subscriberName: string;
  subscriberID: string;
  relationship: string;
  type: string;
}

interface BenefitEntry {
  carrier: string;
  employer: string;
  feeSchedule: string;
  groupNumber: string;
  groupName: string;
  amount: string;
  timePeriod: string;
  quantity: string;
}

interface ProcessedInsuranceWrapper {
  generalInfo: GeneralInfo;
  coveragePercentages: { service: string; percentage: string }[];
  deductibles: { category: string; amount: string; timePeriod: string }[];
  limitations: string[];
}

@Component({
  selector: 'app-insurance-information',
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    MatIconModule,
    MatTooltipModule
  ],
  templateUrl: './insurance-information.component.html',
  styleUrls: ['./insurance-information.component.scss']
})
export class InsuranceInformationComponent implements OnInit {
  insuranceInfo: { generalInfo: GeneralInfo; benefitsBreakdown: BenefitEntry[] }[] = [];
  processedInsuranceInfo: ProcessedInsuranceWrapper[] = [];
  formConfigService = inject(FormConfigService);
  appStateService = inject(AppStateService);
  router = inject(Router);
  clientId: string = '';
  patientId: string = '';
  loading = false;

  ngOnInit(): void {
    this.clientId = this.appStateService.getState().clientId || '';
    this.patientId = this.appStateService.getState().patientId || '';
    this.fetchInsuranceBenefits();
  }

  fetchInsuranceBenefits(): void {
    this.loading = true;
    this.formConfigService.fetchInsuranceBenefits(this.clientId, this.patientId).subscribe({
      next: (data) => {
        this.insuranceInfo = data || [];
        this.processedInsuranceInfo = this.insuranceInfo.map(insurance => ({
          generalInfo: insurance.generalInfo,
          coveragePercentages: this.getCoveragePercentages(insurance.benefitsBreakdown),
          deductibles: this.getDeductibles(insurance.benefitsBreakdown),
          limitations: this.getLimitations(insurance.benefitsBreakdown)
        }));
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        this.insuranceInfo = [];
        this.processedInsuranceInfo = [];
      }
    });
  }

  private getCoveragePercentages(benefits: BenefitEntry[]): { service: string; percentage: string }[] {
    return benefits
      .filter(entry => entry.feeSchedule === "%" && entry.groupName)
      .map(entry => ({ service: entry.groupNumber, percentage: entry.groupName }));
  }

  private getDeductibles(benefits: BenefitEntry[]): { category: string; amount: string; timePeriod: string }[] {
    return benefits
      .filter(entry => entry.feeSchedule === "Deductible" && entry.amount)
      .map(entry => ({ category: entry.groupNumber, amount: entry.amount, timePeriod: entry.timePeriod }));
  }

  private getLimitations(benefits: BenefitEntry[]): string[] {
    const limitations: string[] = [];
    benefits
      .filter(entry => entry.feeSchedule === "Limitation")
      .forEach(entry => {
        if (entry.amount) {
          const prefix = entry.groupNumber !== "General" ? `${entry.groupNumber} ` : "";
          if (entry.timePeriod === "Lifetime") {
            limitations.push(`${prefix}Lifetime Maximum: $${entry.amount}`);
          } else if (entry.timePeriod === "CalendarYear") {
            limitations.push(`${prefix}Annual Maximum: $${entry.amount}`);
          }
        } else if (entry.quantity) {
          if (entry.quantity.includes("NumberOfServices")) {
            const num = entry.quantity.split(" ")[0];
            const service = entry.groupNumber !== "General" ? entry.groupNumber : "Services";
            limitations.push(`${service}: ${num} per ${entry.timePeriod}`);
          } else if (entry.quantity.includes("AgeLimit")) {
            const age = entry.quantity.split(" ")[0];
            limitations.push(`Age Limit: ${age} years for certain benefits`);
          } else if (entry.quantity.includes("Months")) {
            limitations.push(`Waiting Period: ${entry.quantity} for certain services`);
          } else if (entry.quantity.includes("Years")) {
            limitations.push(`Replacement Frequency: ${entry.quantity} for certain items`);
          } else {
            limitations.push(entry.quantity);
          }
        }
      });
    return limitations;
  }


  // Navigate to insurance management screen
  manageInsurance(): void {
    this.router.navigate(['/dashboard/insurance/manage']);
  }

  // Updated generatePdf method that calls the PDF generator service
  generatePdf(): void {
    InsurancePdfGenerator.generateInsurancePDF(this.processedInsuranceInfo);
  }
}